import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/** Opens a create dialog when the URL carries `?new` — the mechanism behind the
 *  ⌘K palette's "Create new …" quick-links (all create forms are modal-only, so
 *  the palette navigates to the list route with `?new` instead of a create URL).
 *
 *  The param is consumed immediately (history `replace`, other params kept) so
 *  it doesn't linger in the URL, re-fire on the next render, or add a back-button
 *  step. Pass `value` on pages with more than one create dialog to react only to
 *  a specific `?new=<value>` (e.g. `?new=item` vs `?new=category`); omit it for
 *  the common single-dialog `?new=1` case.
 */
export function useCreateIntent(onOpen: () => void, value?: string) {
  const [params, setParams] = useSearchParams();
  const raw = params.get('new');
  useEffect(() => {
    if (raw == null) return;
    if (value != null && raw !== value) return;
    onOpen();
    const next = new URLSearchParams(params);
    next.delete('new');
    setParams(next, { replace: true });
    // Fire once per appearance of the param; onOpen/params are stable enough
    // that keying on the raw value is what we want.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw, value]);
}
